import { useId, useMemo, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import { ArrowRight, CalendarClock, ChartBar, Clapperboard, Image as ImageIcon, ImagePlus, Lock, Megaphone, Minus, Plus, Radio, Scissors, Send, Sparkles, Trash2, X, Youtube } from "lucide-react";
import { upcomingLives, YOUTUBE_LIVE_URL } from "@/lib/schedule";
import { CHELSEA, CLASSES, LIVE_MS, PLANS, PROJECTS, countComments, replays, type Role } from "./sample";
import { JoinButton, M, MembersPage, joinCta, useRole } from "./Members";
import { Part } from "../Page";
import { Draw, EASE, Head, datePart } from "../bits";
import {
  Avatar, KIND, PostCard, SideCard, ToastHost, VisPicker, canSee, postStore, published, toast, useHref, usePosts,
  type Kind, type LivePost, type Vis,
} from "./PostCard";
import "./Posts.css";

/* The club's posts feed (/blue-2/members/posts): Chelsea's posts in a
   Patreon-style feed with filters, a pinned post, likes, polls, and locked
   cards for people who can't open a post yet. In Chelsea's view a composer
   sits on top and every post gets pin / edit / delete. All local state. */

type Filter = "all" | Exclude<Kind, "update">;
const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "All" }, { key: "project", label: "Projects" }, { key: "replay", label: "Replays" },
  { key: "poll", label: "Polls" }, { key: "photo", label: "Photos" },
];

const dayKey = (d: Date) => datePart(d, { year: "numeric", month: "2-digit", day: "2-digit" });
/** "Tonight", "Tomorrow" or "Thu, Oct 15", in Chelsea's time zone. */
export function whenLabel(d: Date, now = new Date()) {
  if (now >= d && now.getTime() < d.getTime() + LIVE_MS) return "Live now";
  if (dayKey(d) === dayKey(now)) return "Tonight";
  if (dayKey(d) === dayKey(new Date(now.getTime() + 864e5))) return "Tomorrow";
  return datePart(d, { weekday: "short", month: "short", day: "numeric" });
}
const classOn = (d: Date) => CLASSES.find(c => c.date === datePart(d, { month: "short", day: "numeric", year: "numeric" }));

/* ─── Header ─── */

function Header({ count, polls }: { count: number; polls: number }) {
  const href = useHref();
  const next = upcomingLives(1, "club")[0]?.start;
  const k = next && classOn(next);
  return <div className="mp-head">
    <div className="mp-head-copy">
      <motion.p className="mp-kicker" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>Loose Threads Club · posts</motion.p>
      <h1 className="mp-h1" aria-label="Posts, fresh from the studio">
        <motion.span className="mp-h1-hand" aria-hidden="true" initial={{ opacity: 0, rotate: -12 }} animate={{ opacity: 1, rotate: -5 }} transition={{ delay: 0.45, duration: 0.5, ease: EASE }}>fresh from the</motion.span>
        <span className="mp-h1-word" aria-hidden="true">
          {"studio".split("").map((c, i) => <motion.span key={i} initial={{ y: "18%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }} transition={{ delay: 0.1 + i * 0.05, duration: 0.6, ease: EASE }}>{c}</motion.span>)}
        </span>
        <svg className="mp-h1-doodle" viewBox="0 0 220 30" aria-hidden="true"><Draw d="M4 18 C 40 6, 70 26, 108 14 S 176 6, 214 16" delay={0.9} width={4} /></svg>
      </h1>
    </div>
    <motion.div className="mp-head-side" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.5, ease: EASE }}>
      <p className="mp-lede">Project news, class replays, polls, and the occasional Cosmo photo. Straight from my sewing table to you.</p>
      <ul className="mp-stats" aria-label="This month">
        <li><b>{count}</b> posts</li>
        <li><b>{CLASSES.filter(c => c.projectId === PROJECTS[0].id).length}</b> live classes</li>
        {polls > 0 && <li><b>{polls}</b> {polls === 1 ? "poll" : "polls"} open</li>}
      </ul>
      {next && <Link className="mp-next" to={href(M.live)}>
        <span className="mp-next-cal" aria-hidden="true"><small>{datePart(next, { weekday: "short" })}</small><b>{datePart(next, { day: "numeric" })}</b></span>
        <span className="mp-next-txt">
          <em>Next club class</em>
          <strong>{whenLabel(next)} · 7 PM MT</strong>
          {k && <span>{PROJECTS.find(p => p.id === k.projectId)?.title}, {k.part.toLowerCase()}</span>}
        </span>
        <ArrowRight size={18} aria-hidden="true" />
      </Link>}
    </motion.div>
  </div>;
}

/* ─── Composer (Chelsea) ─── */

const KINDS: { key: Kind; label: string; Icon: typeof Megaphone }[] = [
  { key: "update", label: "Update", Icon: Megaphone }, { key: "photo", label: "Photo", Icon: ImageIcon }, { key: "poll", label: "Poll", Icon: ChartBar },
  { key: "project", label: "Project", Icon: Scissors }, { key: "replay", label: "Replay", Icon: Clapperboard },
];
const SAMPLE_PHOTOS = ["/chelsea-and-cosmo.jpg", "/chelsea.JPG", "/chelsea-rainbow-portrait.jpg", "/video/live-class.jpg"];
const toLocalInput = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 6e4).toISOString().slice(0, 16);
const tomorrowAt9 = () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return toLocalInput(d); };
export const fmtWhen = (local: string) => new Date(local).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

type Draft = { kind: Kind; title: string; body: string; vis: Vis; image?: string; alt: string; options: string[]; projectId: string; classId: string; when: "now" | "later"; at: string; pin: boolean; notify: boolean };
const blank = (kind: Kind = "update"): Draft => ({
  kind, title: "", body: "", vis: kind === "replay" ? "members" : "public", alt: "", options: ["", ""], projectId: PROJECTS[0].id, classId: replays()[0]?.id ?? "",
  when: "now", at: tomorrowAt9(), pin: false, notify: true,
});

function Composer() {
  const href = useHref();
  const id = useId();
  const [open, setOpen] = useState(false);
  const [d, setD] = useState<Draft>(blank);
  const [tried, setTried] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD(x => ({ ...x, [k]: v }));

  const start = (kind?: Kind) => {
    setD(x => ({ ...x, kind: kind ?? x.kind, vis: kind === "replay" ? "members" : x.vis }));
    setOpen(true);
    requestAnimationFrame(() => titleRef.current?.focus());
  };
  const close = () => { setOpen(false); setTried(false); setD(blank()); requestAnimationFrame(() => trigger.current?.focus()); };

  const options = d.options.map(o => o.trim()).filter(Boolean);
  const problem = !d.title.trim() ? "Give it a title." : d.kind === "photo" && !d.image ? "Add a photo for a photo post." : d.kind === "poll" && options.length < 2 ? "A poll needs at least two options." : d.when === "later" && new Date(d.at) <= new Date() ? "Pick a time in the future." : "";

  function submit(e: FormEvent) {
    e.preventDefault();
    setTried(true);
    if (problem) return;
    const later = d.when === "later";
    const post: LivePost = {
      id: `new-${Date.now().toString(36)}`, kind: d.kind, visibility: d.vis, title: d.title.trim(), body: d.body.trim(), ago: "Just now", likes: 0, comments: [],
      images: d.image ? [d.image] : undefined, alts: d.image && d.alt.trim() ? { [d.image]: d.alt.trim() } : undefined,
      poll: d.kind === "poll" ? { q: d.title.trim(), options: options.map(label => ({ label, votes: 0 })) } : undefined,
      projectId: d.kind === "project" ? d.projectId : undefined, classId: d.kind === "replay" ? d.classId : undefined,
      scheduled: later ? d.at : undefined,
    };
    postStore.add(post);
    if (d.pin && !later) postStore.pin(post.id);
    toast(later
      ? { text: `Scheduled for ${fmtWhen(d.at)}. It’s waiting above your feed.` }
      : { text: d.notify ? `Posted! ${d.vis === "members" ? "Members get" : d.vis === "friends" ? "Thread Friends and members get" : "Everyone in the club gets"} a heads-up by email.` : "Posted to the club.", link: { to: href(M.post(post.id)), label: "View post" } });
    close();
  }

  const onFile = (f?: File) => { if (f) { set("image", URL.createObjectURL(f)); set("alt", ""); } };

  return <section className="mp-compose" aria-label="Write a post">
    {!open ? <div className="mp-compose-closed">
      <Avatar who={CHELSEA} size={46} />
      <button ref={trigger} type="button" className="mp-compose-fake" onClick={() => start()}>Share something with the club…</button>
      <div className="mp-compose-quick" role="group" aria-label="Start a post as">
        {KINDS.filter(k => k.key !== "update").map(({ key, label, Icon }) => <button key={key} type="button" onClick={() => start(key)}><Icon size={16} aria-hidden="true" /> {label}</button>)}
      </div>
    </div> : <motion.form className="mp-compose-form" onSubmit={submit} noValidate initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, ease: EASE }}
      onKeyDown={e => { if (e.key === "Escape") close(); }}>
      <div className="mp-compose-top">
        <Avatar who={CHELSEA} size={42} />
        <p className="mp-compose-hand">new post!</p>
        <button type="button" className="mp-icon-btn" onClick={close} aria-label="Close without posting"><X size={20} aria-hidden="true" /></button>
      </div>

      <fieldset className="mp-pick">
        <legend>What kind of post</legend>
        <div className="mp-pick-row">
          {KINDS.map(({ key, label, Icon }) => <label key={key} className={`mp-pick-chip${d.kind === key ? " is-on" : ""}`}>
            <input type="radio" name={`${id}-kind`} checked={d.kind === key} onChange={() => setD(x => ({ ...x, kind: key, vis: key === "replay" ? "members" : x.vis }))} />
            <Icon size={14} aria-hidden="true" /> {label}
          </label>)}
        </div>
      </fieldset>

      <label className="mp-field"><span>{d.kind === "poll" ? "Question" : "Title"}</span>
        <input ref={titleRef} value={d.title} onChange={e => set("title", e.target.value)} placeholder={d.kind === "poll" ? "What should we make next?" : "Give it a title"} aria-invalid={tried && !d.title.trim()} maxLength={120} />
      </label>
      <label className="mp-field"><span>Post</span>
        <textarea value={d.body} rows={3} onChange={e => set("body", e.target.value)} placeholder="Say hi, share news, tell them what to pull from their stash…" />
      </label>

      {d.kind === "poll" && <fieldset className="mp-opts">
        <legend>Options</legend>
        {d.options.map((o, i) => <div key={i} className="mp-opt">
          <label className="mp-sr" htmlFor={`${id}-o${i}`}>Option {i + 1}</label>
          <input id={`${id}-o${i}`} value={o} placeholder={`Option ${i + 1}`} onChange={e => set("options", d.options.map((x, j) => (j === i ? e.target.value : x)))} />
          {d.options.length > 2 && <button type="button" className="mp-icon-btn" aria-label={`Remove option ${i + 1}`} onClick={() => set("options", d.options.filter((_, j) => j !== i))}><Minus size={16} aria-hidden="true" /></button>}
        </div>)}
        {d.options.length < 5 && <button type="button" className="mp-add" onClick={() => set("options", [...d.options, ""])}><Plus size={15} aria-hidden="true" /> Add an option</button>}
      </fieldset>}

      {d.kind === "project" && <label className="mp-field"><span>Project</span>
        <select value={d.projectId} onChange={e => set("projectId", e.target.value)}>{PROJECTS.map(p => <option key={p.id} value={p.id}>{p.title} · {p.month}</option>)}</select>
      </label>}
      {d.kind === "replay" && <label className="mp-field"><span>Class replay <small>(only classes that have aired)</small></span>
        <select value={d.classId} onChange={e => set("classId", e.target.value)}>{replays().map(c => <option key={c.id} value={c.id}>{c.title} · {c.part} · {c.date}</option>)}</select>
      </label>}

      {d.kind !== "replay" && d.kind !== "poll" && <div className="mp-attach">
        {d.image ? <div className="mp-attach-on">
          <img src={d.image} alt="" />
          <div>
            <label className="mp-field"><span>Describe the photo <small>(for screen readers)</small></span>
              <input value={d.alt} onChange={e => set("alt", e.target.value)} placeholder="e.g. Cosmo asleep on a pile of fat quarters" />
            </label>
            <button type="button" className="mp-add" onClick={() => set("image", undefined)}><Trash2 size={15} aria-hidden="true" /> Remove photo</button>
          </div>
        </div> : <>
          <button type="button" className="mp-attach-drop" onClick={() => fileRef.current?.click()}
            onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); onFile(e.dataTransfer.files[0]); }}>
            <ImagePlus size={22} aria-hidden="true" /><span><b>Add a photo</b> or drop one here</span><small>Stays on this device; nothing uploads in the prototype.</small>
          </button>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={e => onFile(e.target.files?.[0])} />
          <div className="mp-attach-samples" role="group" aria-label="Or use a sample photo">
            <span>or try:</span>
            {SAMPLE_PHOTOS.map(src => <button key={src} type="button" onClick={() => set("image", src)} aria-label="Use this sample photo"><img src={src} alt="" /></button>)}
          </div>
        </>}
      </div>}

      <VisPicker value={d.vis} onChange={v => set("vis", v)} name={`${id}-vis`} />

      <fieldset className="mp-pick">
        <legend>When</legend>
        <div className="mp-pick-row">
          <label className={`mp-pick-chip${d.when === "now" ? " is-on" : ""}`}><input type="radio" name={`${id}-when`} checked={d.when === "now"} onChange={() => set("when", "now")} /><Send size={14} aria-hidden="true" /> Post now</label>
          <label className={`mp-pick-chip${d.when === "later" ? " is-on" : ""}`}><input type="radio" name={`${id}-when`} checked={d.when === "later"} onChange={() => set("when", "later")} /><CalendarClock size={14} aria-hidden="true" /> Schedule</label>
          {d.when === "later" && <label className="mp-when"><span className="mp-sr">Date and time</span><input type="datetime-local" value={d.at} min={toLocalInput(new Date())} onChange={e => set("at", e.target.value)} /></label>}
        </div>
      </fieldset>

      <div className="mp-checks">
        <label><input type="checkbox" checked={d.pin} onChange={e => set("pin", e.target.checked)} disabled={d.when === "later"} /> Pin to the top</label>
        <label><input type="checkbox" checked={d.notify} onChange={e => set("notify", e.target.checked)} /> Email {d.vis === "members" ? "members" : "everyone who can see it"}</label>
      </div>

      <div className="mp-compose-foot">
        <p className="mp-compose-err" role="alert">{tried && problem}</p>
        <button type="button" className="mb-btn mb-btn-line" onClick={close}>Cancel</button>
        <button type="submit" className="mb-btn mb-btn-ink">{d.when === "later" ? <><CalendarClock size={16} aria-hidden="true" /> Schedule</> : <><Send size={16} aria-hidden="true" /> Post</>}</button>
      </div>
    </motion.form>}
  </section>;
}

function Scheduled({ list }: { list: LivePost[] }) {
  if (!list.length) return null;
  return <section className="mp-sched" aria-label="Scheduled posts">
    <p className="mp-sched-head"><CalendarClock size={16} aria-hidden="true" /> Scheduled <span>only you can see these</span></p>
    <ul>
      <AnimatePresence initial={false}>
        {list.map(p => <motion.li key={p.id} layout initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
          <div className="mp-sched-row">
            <span className="mp-sched-txt"><small>{KIND[p.kind]} · goes out {fmtWhen(p.scheduled!)}</small><b>{p.title}</b></span>
            <button type="button" className="mb-btn mb-btn-ink mb-btn-sm" onClick={() => { postStore.patch(p.id, x => ({ ...x, scheduled: undefined, ago: "Just now" })); toast({ text: "Posted now instead." }); }}>Post now</button>
            <button type="button" className="mp-icon-btn" aria-label={`Delete scheduled post “${p.title}”`} onClick={() => { const undo = postStore.remove(p.id); toast({ text: "Scheduled post deleted.", action: { label: "Undo", run: undo } }); }}><Trash2 size={17} aria-hidden="true" /></button>
          </div>
        </motion.li>)}
      </AnimatePresence>
    </ul>
  </section>;
}

/* ─── Sidebar ─── */

export function RoleCard({ role, live }: { role: Role; live: LivePost[] }) {
  const href = useHref();
  const id = useId();
  const next = upcomingLives(1, "club")[0]?.start;
  if (role === "chelsea") {
    const likes = live.reduce((n, p) => n + p.likes, 0);
    const comments = live.reduce((n, p) => n + countComments(p, true), 0);
    return <SideCard className="mp-role mp-role-chelsea" labelledBy={id}>
      <p className="mp-side-kicker">Your studio</p>
      <h2 id={id} className="mp-side-h">This month so far</h2>
      <dl className="mp-role-stats">
        <div><dt>posts</dt><dd>{live.length}</dd></div>
        <div><dt>likes</dt><dd>{likes}</dd></div>
        <div><dt>comments</dt><dd>{comments}</dd></div>
      </dl>
      <Link className="mb-btn mb-btn-pop" to={href(M.live)}><Radio size={16} aria-hidden="true" /> Go live</Link>
    </SideCard>;
  }
  if (role === "member") return <SideCard className="mp-role" labelledBy={id}>
    <p className="mp-side-kicker">Member</p>
    <h2 id={id} className="mp-side-h">You’re in! <span aria-hidden="true">♥</span></h2>
    <p className="mp-side-p">{next ? `Class is ${whenLabel(next).toLowerCase() === "tonight" ? "tonight" : `on ${whenLabel(next)}`} at 7 PM MT. Grab your fat quarters.` : "Every replay is waiting in Classes."}</p>
    <div className="mp-side-acts">
      <Link className="mb-btn mb-btn-pop" to={href(M.live)}>Live room <ArrowRight size={16} aria-hidden="true" /></Link>
      <Link className="mp-link" to={href(M.classes)}>All replays</Link>
    </div>
  </SideCard>;
  const friend = role === "friend";
  return <SideCard className="mp-role" labelledBy={id}>
    <p className="mp-side-kicker">{friend ? "Thread Friend" : "Peeking in?"}</p>
    <h2 id={id} className="mp-side-h">{friend ? "Thank you for keeping the lights on" : "Sew with me, not just scroll"}</h2>
    <p className="mp-side-p">{friend
      ? "You see public and Thread Friend posts. Members also get two live classes a month and every replay."
      : `Members get two live classes a month, every replay, and every post. Or support the studio as a ${PLANS.friend.name} for ${PLANS.friend.per}.`}</p>
    <div className="mp-side-acts"><JoinButton variant="pop" /></div>
  </SideCard>;
}

export function Calendar() {
  const href = useHref();
  const id = useId();
  const lives = upcomingLives(3);
  return <SideCard className="mp-cal" labelledBy={id}>
    <p className="mp-side-kicker">On the calendar</p>
    <h2 id={id} className="mp-side-h">Next lives</h2>
    <ul>
      {lives.map(l => { const club = l.kind === "club"; const k = club ? classOn(l.start) : undefined;
        const inner = <>
          <span className="mp-cal-day" aria-hidden="true"><small>{datePart(l.start, { month: "short" })}</small><b>{datePart(l.start, { day: "numeric" })}</b></span>
          <span className="mp-cal-txt">
            <strong>{club ? <><Lock size={12} aria-hidden="true" /> Club class</> : <><Youtube size={13} aria-hidden="true" /> Free on YouTube</>}</strong>
            <span>{whenLabel(l.start)} · 7 PM MT{k ? ` · ${k.part.replace(/ of \d+/, "")}` : ""}</span>
          </span>
        </>;
        return <li key={l.start.toISOString()} className={club ? "is-club" : "is-yt"}>
          {club ? <Link to={href(M.live)}>{inner}</Link> : <a href={YOUTUBE_LIVE_URL} target="_blank" rel="noreferrer">{inner}<span className="mp-sr"> (opens YouTube)</span></a>}
        </li>; })}
    </ul>
    <p className="mp-cal-note">Most Tuesdays I’m live on YouTube, free. Come say hi!</p>
  </SideCard>;
}

function ProjectNote() {
  const href = useHref();
  const p = PROJECTS[0];
  const first = CLASSES.find(c => c.projectId === p.id);
  return <Link className="mp-side-project" to={href(first ? M.klass(first.id) : M.classes)}>
    <i className="mp-tape mp-tape-soft" aria-hidden="true" />
    <img src={p.image} alt="" />
    <span className="mp-side-project-txt"><small>{p.month.split(" ")[0]}’s project</small><b>{p.title}</b><span>{p.skill} · supplies & pattern <ArrowRight size={13} aria-hidden="true" /></span></span>
  </Link>;
}

/* ─── Feed ─── */

function Feed() {
  const role = useRole();
  const href = useHref();
  const all = usePosts();
  const [filter, setFilter] = useState<Filter>("all");
  const live = useMemo(() => published(all), [all]);
  const scheduled = all.filter(p => p.scheduled);
  const shown = filter === "all" ? live : live.filter(p => p.kind === filter);
  const hidden = live.filter(p => !canSee(role, p.visibility)).length;
  // The first locked post explains itself in full; later ones get a slim lock row.
  const firstLocked = shown.findIndex(p => !canSee(role, p.visibility));
  const count = (f: Filter) => (f === "all" ? live.length : live.filter(p => p.kind === f).length);
  const cta = joinCta(role);

  return <section className="mp-feed" aria-labelledby="mp-feed-title">
    <div className="mp-grid">
      <div className="mp-main">
        {role === "chelsea" && <><Composer /><Scheduled list={scheduled} /></>}
        <div className="mp-bar">
          <Head id="mp-feed-title" hand="latest" word="posts" tone="blue" className="mb-headsm mp-feed-h" />
          <div className="mp-filters" role="group" aria-label="Show">
            {FILTERS.map(f => <button key={f.key} type="button" aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>{f.label}<small>{count(f.key)}</small></button>)}
          </div>
        </div>
        {hidden > 0 && filter === "all" && cta && <p className="mp-peek">
          <Sparkles size={16} aria-hidden="true" />
          <span>{role === "visitor" ? `You’re seeing what’s public. ${hidden} more ${hidden === 1 ? "post is" : "posts are"} for supporters.` : `${hidden} ${hidden === 1 ? "post is" : "posts are"} for members, with the classes and replays.`}</span>
          <Link to={cta.to}>{cta.short} <ArrowRight size={14} aria-hidden="true" /></Link>
        </p>}
        <p className="mp-sr" role="status">{filter === "all" ? `Showing all ${shown.length} posts` : `${FILTERS.find(f => f.key === filter)?.label}: ${shown.length} ${shown.length === 1 ? "post" : "posts"}`}</p>
          <div className="mp-list">
            <AnimatePresence mode="popLayout" initial={false}>
              {shown.map((p, i) => <PostCard key={p.id} post={p} index={i} compactLock={i > firstLocked} />)}
            </AnimatePresence>
            {shown.length === 0 && <motion.div className="mp-empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <p className="mp-empty-hand">nothing here yet!</p>
              <p>No {FILTERS.find(f => f.key === filter)?.label.toLowerCase()} right now.</p>
              <button type="button" className="mb-btn mb-btn-ink" onClick={() => setFilter("all")}>Show all posts</button>
            </motion.div>}
          </div>
        {shown.length > 0 && <p className="mp-end">that’s everything for now <span aria-hidden="true">✂︎</span></p>}
      </div>
      <aside className="mp-side" aria-label="Club at a glance">
        <Part p="club"><RoleCard role={role} live={live} /></Part>
        <div className="mp-side-stick">
          <Part p="making"><Calendar /></Part>
          <ProjectNote />
        </div>
      </aside>
    </div>
  </section>;
}

function HeaderWithCount() {
  const all = usePosts();
  const live = published(all);
  return <Header count={live.length} polls={live.filter(p => p.kind === "poll").length} />;
}

export function MembersPosts() {
  return <MembersPage page="posts" header={<MotionConfig reducedMotion="user"><HeaderWithCount /></MotionConfig>}>
    <MotionConfig reducedMotion="user">
      <Part p="faves"><Feed /></Part>
      <ToastHost />
    </MotionConfig>
  </MembersPage>;
}
